import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTimer } from 'react-timer-hook'
import './StyleComponents/DigitalTestingLab.css'

export default function DigitalTestingLab({ test: testWrapper, onExit }) {
    // State to hold the dynamic 4-section array built from flat questions
    const [structuredSections, setStructuredSections] = useState([[], [], [], []])

    const testSession = testWrapper?.session

    const [showInstructions, setShowInstructions] = useState(false)
    const [showFormulas, setShowFormulas] = useState(false)
    const [showFRQInstructions, setShowFRQInstructions] = useState(false)
    const [showExitConfirm, setShowExitConfirm] = useState(false)
    
    // Timing and Mode Selection States
    const [timeMultiplier, setTimeMultiplier] = useState(1.0)
    const [isUntimed, setIsUntimed] = useState(false)
    const [modeSelected, setModeSelected] = useState(false)
    const [englishTime, setEnglishTime] = useState(32 * 60)
    const [mathTime, setMathTime] = useState(35 * 60)
    const [breakTime, setBreakTime] = useState(5 * 60)

    const [qIndex, setQIndex] = useState(-1)
    const [studentAnswers, setStudentAnswers] = useState([[], [], [], []])
    const [flaggedQuestions, setFlaggedQuestions] = useState([[], [], [], []])
    const [mIndex, setMIndex] = useState(-1)
    const [openConfirm, setOpenConfirm] = useState(false)
    const [countLeaves, setCountLeaves] = useState(0)
    const [isDrawing, setIsDrawing] = useState(false)

    const navigate = useNavigate()
    const imageRef = useRef(null)
    const canvasRef = useRef(null)
    const contextRef = useRef(null)

    // Build the flexible section matrices from the raw flat questions array
    useEffect(() => {
        const rawQuestions = testWrapper?.digitalTest?.questions
        if (!rawQuestions || !Array.isArray(rawQuestions)) return

        // 1. Group questions into their respective buckets
        const sections = [[], [], [], []]
        
        rawQuestions.forEach(q => {
            const isMath = q.mr?.toLowerCase() === "math"
            const modNum = parseInt(q.module, 10)

            if (!isMath && modNum === 1) sections[0].push(q)
            else if (!isMath && modNum === 2) sections[1].push(q)
            else if (isMath && modNum === 1) sections[2].push(q)
            else if (isMath && modNum === 2) sections[3].push(q)
        })

        // 2. Sort each module sequentially by question number attribute
        sections.forEach(modQuestions => {
            modQuestions.sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10))
        })

        setStructuredSections(sections)

        // 3. Initialize answer and flag matrices matching the built capacities
        let initialAnswers = sections.map(modQuestions => modQuestions.map(() => ""))
        let initialFlags = sections.map(modQuestions => modQuestions.map(() => false))
        let targetMIndex = -1

        // 4. Restore state if an ongoing session is active
        if (testSession) {
            const savedAnswersSource = [
                testSession.RWMod1,
                testSession.RWMod2,
                testSession.MathMod1,
                testSession.MathMod2
            ]

            initialAnswers = sections.map((modQuestions, mIdx) => {
                const savedStr = savedAnswersSource[mIdx]
                const savedArr = savedStr ? savedStr.split(",") : []
                return modQuestions.map((_, qIdx) => savedArr[qIdx] || "")
            })

            if (testSession.flagged_questions) {
                const flagSections = testSession.flagged_questions.split("~")
                initialFlags = sections.map((modQuestions, mIdx) => {
                    const sectionStr = flagSections[mIdx]
                    const sectionArr = sectionStr ? sectionStr.split(",") : []
                    return modQuestions.map((_, qIdx) => sectionArr[qIdx] === "true")
                })
            }

            if (testSession.timing_config === "UNTIMED") {
                setIsUntimed(true)
            } else {
                let multiplier = 1.0
                if (testSession.timing_config === "FIFTY_PERCENT") multiplier = 1.5
                else if (testSession.timing_config === "ONE_HUNDRED_PERCENT") multiplier = 2.0

                setTimeMultiplier(multiplier)
                setEnglishTime(32 * 60 * multiplier)
                setMathTime(35 * 60 * multiplier)
                setBreakTime(5 * 60 * multiplier)
            }

            targetMIndex = testSession.current_section_id !== undefined ? testSession.current_section_id : 0
            setMIndex(targetMIndex)
            setModeSelected(true)
        }

        setStudentAnswers(initialAnswers)
        setFlaggedQuestions(initialFlags)
        setQIndex(0)
    }, [testWrapper, testSession])

    const handleConfirmMode = (multiplier, untimedMode) => {
        let calcEnglish = 32 * 60
        let calcMath = 35 * 60
        let calcBreak = 5 * 60

        if (!untimedMode) {
            calcEnglish *= multiplier
            calcMath *= multiplier
            calcBreak *= multiplier
            
            setTimeMultiplier(multiplier)
            setEnglishTime(calcEnglish)
            setMathTime(calcMath)
            setBreakTime(calcBreak)
            setIsUntimed(false)
        } else {
            setIsUntimed(true)
        }

        setMIndex(0)
        setModeSelected(true)
        setShowInstructions(true)
    }

    // Timer setup
    const { seconds, minutes, restart } = useTimer({
        onExpire: () => {
            if (isUntimed) return
            if (!showInstructions) confirmSubmitModule()
            else setShowInstructions(false)
        }
    })

    // Sync durations dynamically
    useEffect(() => {
        if (mIndex > -1 && modeSelected && !isUntimed) {
            const time = new Date()
            if (!showInstructions) {
                if (testSession && testSession.current_section_id === mIndex && testSession.time_remaining_seconds > 0) {
                    time.setSeconds(time.getSeconds() + testSession.time_remaining_seconds)
                    testSession.time_remaining_seconds = 0 
                } else {
                    time.setSeconds(time.getSeconds() + (mIndex < 2 ? englishTime : mathTime))
                }
                restart(time)
            } else {
                time.setSeconds(time.getSeconds() + breakTime)
                restart(time)
            }
        }
    }, [mIndex, showInstructions, modeSelected, isUntimed])

    const handleExitActions = () => {
        if (onExit) onExit()
        else navigate("/Dashboard")
    }

    const confirmSubmitModule = () => {
        if (mIndex === 3) {
            alert("Test finished! View your analysis in your score archive.")
            handleExitActions()
        } else {
            setMIndex(mIndex + 1)
            setQIndex(0)
            setShowInstructions(true)
        }
    }

    const changeCanvas = () => {
        if (imageRef.current !== null) {
            const canvas = canvasRef.current
            canvas.width = imageRef.current.offsetWidth * 2
            canvas.height = imageRef.current.offsetHeight * 2
            canvas.style.width = imageRef.current.offsetWidth + "px"
            canvas.style.height = imageRef.current.offsetHeight + "px"
            const context = canvas.getContext("2d")
            context.scale(2, 2)
            context.strokeStyle = "yellow"
            context.lineWidth = 15
            context.globalAlpha = 0.6
            context.globalCompositeOperation = "xor"
            contextRef.current = context
        }
    }

    const startDrawing = ({ nativeEvent }) => {
        if (!contextRef.current) return
        contextRef.current.beginPath()
        if (nativeEvent.touches) {
            const canvasRect = canvasRef.current.getBoundingClientRect()
            const canvasX = (nativeEvent.changedTouches[0].clientX - canvasRect.left)
            const canvasY = (nativeEvent.changedTouches[0].clientY - canvasRect.top)
            contextRef.current.moveTo(canvasX, canvasY)
        } else {
            const { offsetX, offsetY } = nativeEvent
            contextRef.current.moveTo(offsetX, offsetY)
        }
        setIsDrawing(true)
    }

    const finishDrawing = () => {
        if (contextRef.current) contextRef.current.closePath()
        setIsDrawing(false)
    }

    const clearCanvas = () => {
        const canvas = canvasRef.current
        const context = canvas.getContext("2d")
        context.clearRect(0, 0, canvas.width, canvas.height)
    }

    const draw = ({ nativeEvent }) => {
        if (!isDrawing || !contextRef.current) return
        if (nativeEvent.touches) {
            const canvasRect = canvasRef.current.getBoundingClientRect()
            const canvasX = (nativeEvent.changedTouches[0].clientX - canvasRect.left)
            const canvasY = (nativeEvent.changedTouches[0].clientY - canvasRect.top)
            contextRef.current.lineTo(canvasX, canvasY)
        } else {
            const { offsetX, offsetY } = nativeEvent
            contextRef.current.lineTo(offsetX, offsetY)
        }
        contextRef.current.stroke()
    }

    const alertUser = (e) => {
        e.preventDefault()
        e.returnValue = "Leaving this page may invalidate ongoing metrics."
    }

    const detectLeave = (e) => {
        e.preventDefault()
        if (mIndex > -1 && document.visibilityState !== 'visible') {
            setCountLeaves(prev => prev + 1)
        }
    }

    useEffect(() => {
        window.addEventListener('beforeunload', alertUser)
        window.onpopstate = alertUser
        return () => {
            window.removeEventListener('beforeunload', alertUser)
            window.onpopstate = null
        }
    }, [])

    useEffect(() => {
        document.addEventListener('visibilitychange', detectLeave)
        return () => { document.removeEventListener('visibilitychange', detectLeave) }
    })

    useEffect(() => {
        setOpenConfirm(false)
    }, [qIndex, studentAnswers, mIndex])

    const changeMC = (e) => {
        if (mIndex < 0 || qIndex < 0) return
        const updatedAnswers = [...studentAnswers]
        updatedAnswers[mIndex][qIndex] = e.target.value
        setStudentAnswers(updatedAnswers)
    }

    const changeFRQAnswer = (e) => {
        if (mIndex < 0 || qIndex < 0) return
        const updatedAnswers = [...studentAnswers]
        updatedAnswers[mIndex][qIndex] = e.target.value.replace(/,/g, "")
        setStudentAnswers(updatedAnswers)
    }

    const inc = () => { 
        if (structuredSections?.[mIndex] && qIndex < structuredSections[mIndex].length - 1) setQIndex(qIndex + 1) 
    }
    const dec = () => { 
        if (qIndex > 0) setQIndex(qIndex - 1) 
    }

    const submitModule = () => setOpenConfirm(true)

    function flagQuestion(m, q) {
        if (m < 0 || q < 0) return
        const updatedFlaggedQuestions = [...flaggedQuestions]
        updatedFlaggedQuestions[m][q] = !updatedFlaggedQuestions[m][q]
        setFlaggedQuestions(updatedFlaggedQuestions)
    }

    if (!modeSelected) {
        return (
            <div className="timing-config-panel" style={{ padding: "2rem", textAlign: "center" }}>
                <h2>Practice Lab Setup</h2>
                <p>Select your pacing parameter for this review session:</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "300px", margin: "0 auto" }}>
                    <button onClick={() => handleConfirmMode(1.0, false)}>
                        Standard Time Constraints
                    </button>
                    <button onClick={() => handleConfirmMode(1.5, false)}>
                        50% Extra Time (1.5x)
                    </button>
                    <button onClick={() => handleConfirmMode(2.0, false)}>
                        100% Extra Time (2.0x)
                    </button>
                    <button onClick={() => handleConfirmMode(1.0, true)}>
                        No Time Limit (Untimed Practice)
                    </button>
                </div>
                <hr style={{ margin: "2rem auto", maxWidth: "300px" }} />
                <button 
                    onClick={() => {
                        if (window.confirm("Are you sure you want to cancel setup and return to the dashboard?")) {
                            handleExitActions()
                        }
                    }} 
                    style={{ backgroundColor: "#6c757d", color: "white" }}
                >
                    Cancel & Return
                </button>
            </div>
        )
    }

    // Structural loading guard
    if (mIndex === -1 || qIndex === -1 || !studentAnswers[mIndex] || !structuredSections[mIndex]) {
        return <div style={{ padding: "2rem", textAlign: "center" }}>Loading laboratory resources...</div>
    }

    const activeQuestion = structuredSections[mIndex]?.[qIndex]

    return (
        <>
            <style>{'body { background-color: white; padding-top: 0; user-select: none;}'}</style>
            
            {showExitConfirm && (
                <div className="overlay" style={{ zIndex: 999 }}>
                    <div style={{ backgroundColor: "white", padding: "2rem", borderRadius: "8px", textAlign: "center", color: "black", maxWidth: "400px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                        <h3 style={{ marginTop: 0 }}>Quit Testing Lab?</h3>
                        <p>Are you sure you want to exit to the dashboard? Unsaved problem work will be lost.</p>
                        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                            <button onClick={handleExitActions} style={{ backgroundColor: "#dc3545", color: "white" }}>
                                Exit Session
                            </button>
                            <button onClick={() => setShowExitConfirm(false)} style={{ backgroundColor: "#6c757d", color: "white" }}>
                                Resume Test
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showFormulas && (
                <div className="overlay" onClick={() => setShowFormulas(false)}>
                    <img src="FormulaSheet.png" alt="formulas" />
                </div>
            )}
            {showFRQInstructions && (
                <div className="overlay" onClick={() => setShowFRQInstructions(false)}>
                    <img src="FRQInstructions.png" alt="FRQInstructions" />
                </div>
            )}

            <div className="testing-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0", padding: "0.5rem 1rem" }}>
                <div>
                    {!showInstructions && (
                        <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                            {`${mIndex < 2 ? "Reading/Writing" : "Math"} Module ${mIndex % 2 + 1}`}
                        </span>
                    )}
                    <div className="timer" style={{ display: "inline-block", marginLeft: "1.5rem" }}>
                        {isUntimed ? (
                            <span>Untimed Practice Mode</span>
                        ) : (
                            <>
                                Time Left <span>{minutes}</span>:<span>{seconds < 10 ? ("0" + seconds) : seconds}</span>
                            </>
                        )}
                    </div>
                </div>
                
                <button 
                    className="exit-lab-trigger"
                    onClick={() => setShowExitConfirm(true)}
                    style={{ backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", padding: "0.4rem 1rem", borderRadius: "4px", fontWeight: "600", cursor: "pointer" }}
                >
                    Exit Test
                </button>
            </div>

            {showInstructions ? (
                <div style={{ padding: "2rem" }}>
                    <h1>{mIndex < 2 ? "Reading/Writing Module " + (mIndex + 1) : "Math Module " + (mIndex - 1)}</h1>
                    <button onClick={() => {
                        setShowInstructions(false)
                        if (!isUntimed) {
                            const time = new Date()
                            time.setSeconds(time.getSeconds() + (mIndex < 2 ? englishTime : mathTime))
                            restart(time)
                        }
                    }}>
                        Start Module
                    </button>
                    <div style={{ margin: "2rem 0.5rem 1rem 0.5rem" }}>
                        <span style={{ fontWeight: "bold" }}>Testing Tools and Features:</span><br />
                        <img src="testfeatures.png" style={{ width: "min(100%,40rem)", border: "1px black solid" }} alt="features" />
                    </div>
                </div>
            ) : (
                <div className="digital-testing-wrapper">
                    <div className="digital-testing-container">
                        <div style={{ display: "flex", marginBottom: "1rem", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ textAlign: "left", fontWeight: "bold" }}>{`Q${qIndex + 1}.`}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                className="digital-test-bookmark"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                onClick={() => flagQuestion(mIndex, qIndex)}
                            >
                                <path
                                    d="M6 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v20l-6-3-6 3V2z"
                                    fill={flaggedQuestions[mIndex]?.[qIndex] ? "red" : "none"}
                                />
                            </svg>
                        </div>
                        <canvas
                            onMouseDown={startDrawing}
                            onTouchStart={startDrawing}
                            onMouseUp={finishDrawing}
                            onTouchEnd={finishDrawing}
                            onMouseMove={draw}
                            onTouchMove={draw}
                            onMouseOut={finishDrawing}
                            onTouchCancel={finishDrawing}
                            ref={canvasRef}
                            style={{ backgroundColor: "transparent", position: "absolute", zIndex: "2", touchAction: "none" }}
                        />
                        {activeQuestion && (
                            <img src={activeQuestion.url} alt="" onLoad={changeCanvas} ref={imageRef} />
                        )}
                        <button onClick={clearCanvas}>Clear Drawings</button>
                        {mIndex > 1 && <button onClick={() => setShowFormulas(true)}>Show Formulas</button>}
                    </div>

                    <div className="digital-testing-container">
                        <h3>Pick/Type Your Answers Here</h3>
                        {activeQuestion?.FRQ ? (
                            <div>
                                <label htmlFor={mIndex + "-" + qIndex}>Type your answer: </label>
                                <input
                                    id={mIndex + "-" + qIndex}
                                    value={studentAnswers[mIndex]?.[qIndex] || ""}
                                    onChange={changeFRQAnswer}
                                    maxLength="5"
                                    autoComplete="off"
                                />
                                <p style={{ fontWeight: "bold", color: "darkred" }}>Verify FRQ standards to secure scoring accuracy.</p>
                                <button onClick={() => setShowFRQInstructions(true)}>Show FRQ Instructions</button>
                            </div>
                        ) : (
                            <form onSubmit={(e) => e.preventDefault()}>
                                {["A", "B", "C", "D"].map((answer, i) => (
                                    <React.Fragment key={mIndex + "-" + qIndex + "-" + i}>
                                        <input
                                            type="radio"
                                            className="mc"
                                            id={mIndex + "-" + qIndex + "-" + i}
                                            value={answer}
                                            onChange={changeMC}
                                            checked={studentAnswers[mIndex]?.[qIndex] === answer}
                                        />
                                        <label htmlFor={mIndex + "-" + qIndex + "-" + i}>
                                            {answer}
                                        </label>
                                        <br />
                                    </React.Fragment>
                                ))}
                            </form>
                        )}
                        <button onClick={dec}>previous question</button>
                        <button onClick={inc}>next question</button>
                        <br />
                        <button onClick={submitModule}>submit module</button>
                        {openConfirm && (
                            <>
                                <p style={{ color: "red", fontWeight: "bold" }}>Are you sure you want to submit?</p>
                                <button onClick={confirmSubmitModule}>Confirm Submit</button>
                            </>
                        )}
                    </div>

                    <div>
                        <h3>Your Answers</h3>
                        <div className="student-answers-wrapper">
                            {studentAnswers[mIndex]?.map((answer, i) => (
                                <div
                                    key={mIndex + "/" + i}
                                    className="student-answers-element"
                                    onClick={() => setQIndex(i)}
                                    style={{ color: (flaggedQuestions[mIndex]?.[i] ? "red" : "") }}
                                >
                                    <b style={{ cursor: "pointer" }}>{(i + 1) + ". "}</b>{answer}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}